import React from "react";
import PropTypes from "prop-types";
import { Grid, Card, Stack, Typography, Box, CircularProgress, Tooltip } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { fEnergy, fMoneyShort } from "src/utils/formatNumber";

// Network-wide KPI tiles for the dashboard header.

const Tile = styled(Card, {
  shouldForwardProp: (prop) => prop !== "accent",
})(({ theme, accent }) => ({
  padding: theme.spacing(1.75),
  borderRadius: theme.shape.borderRadiusMd,
  border: `1px solid ${theme.palette.grey[200]}`,
  borderTop: `3px solid ${accent}`,
  boxShadow: theme.customShadows.z1,
  height: "100%",
}));

const fmtNumber = (v) => {
  const n = Number(v) || 0;
  return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : `${n}`;
};
const fmtEnergy = fEnergy;
const fmtMoney = fMoneyShort;

// Human phrase for each period, used in tile labels/tooltips.
const PERIOD_PHRASE = {
  Today: "today",
  Last7Days: "in the last 7 days",
  Last30Days: "in the last 30 days",
  Last90Days: "in the last 90 days",
};

const buildTiles = (s, period = "Today", theme) => {
  const isToday = period === "Today";
  const suffix = isToday ? " Today" : ""; // "Energy Today" vs "Energy"
  const phrase = PERIOD_PHRASE[period] || "in the selected period";
  // In Use and Faulted live in the Chargers & Status section, so they're not repeated here.
  return [
    { key: "energyToday", label: `Energy${suffix}`, value: fmtEnergy(s.energyToday), accent: theme.palette.chart.blue[0], hint: `Energy delivered ${phrase}` },
    { key: "revenueToday", label: `Revenue${suffix}`, value: fmtMoney(s.revenueToday), accent: theme.palette.chart.violet[0], hint: `Revenue collected ${phrase}` },
    { key: "sessionsToday", label: `Sessions${suffix}`, value: fmtNumber(s.sessionsToday), accent: theme.palette.chart.green[0], hint: `Charging sessions started ${phrase}` },
    { key: "failedRateToday", label: "Failed Rate", value: `${Number(s.failedRateToday || 0).toFixed(1)}%`, accent: theme.palette.warning.main, hint: `Share of sessions that failed (${phrase})` },
    { key: "openTickets", label: "Open Tickets", value: fmtNumber(s.openTickets), accent: theme.palette.text.secondary, hint: "Open support tickets (team-level, current)" },
  ];
};

const NetworkKpiRow = ({ summary, isLoading = false, onTileClick, clickableKeys = [], period = "Today" }) => {
  const theme = useTheme();
  const s = summary || {};
  const tiles = buildTiles(s, period, theme);

  return (
    <Grid container spacing={{ xs: 1.5, sm: 2 }}>
      {tiles.map((t, i) => {
        const clickable = typeof onTileClick === "function" && clickableKeys.includes(t.key);
        return (
        // An odd last tile spans the full row on phones rather than sitting alone.
        <Grid item xs={i === tiles.length - 1 && tiles.length % 2 ? 12 : 6} sm={4} md={12 / tiles.length} key={t.key}>
          <Tooltip title={clickable ? `${t.hint} — click to view` : t.hint} arrow placement="top">
            <Tile
              accent={t.accent}
              onClick={clickable ? () => onTileClick(t.key) : undefined}
              sx={clickable ? { cursor: "pointer", "&:hover": { boxShadow: 4 } } : undefined}
            >
              <Stack spacing={0.5}>
                <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 500, whiteSpace: "nowrap" }}>
                  {t.label}
                </Typography>
                {isLoading ? (
                  <Box sx={{ py: 0.5 }}>
                    <CircularProgress size={20} />
                  </Box>
                ) : (
                  <Typography variant="h5" sx={{ fontWeight: 700, color: t.accent }}>
                    {t.value}
                  </Typography>
                )}
              </Stack>
            </Tile>
          </Tooltip>
        </Grid>
        );
      })}
    </Grid>
  );
};

NetworkKpiRow.propTypes = {
  summary: PropTypes.object,
  isLoading: PropTypes.bool,
  onTileClick: PropTypes.func,
  clickableKeys: PropTypes.array,
  period: PropTypes.string,
};

export default NetworkKpiRow;
